import React, { useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { rectSwappingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useFormContext } from 'react-hook-form';
import { AdImagesItem } from './AdImagesItem';
import * as S from './AdImages.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { PresetPhotoPicker } from '@/components/PresetPhotoPicker/PresetPhotoPicker';
import {
  AdImage,
  AdImagesProps,
  AdImagesRef,
} from '@/components/AdImages/AdImages.types';
import { useImagesList } from '@/hooks/useImagesList';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { Button } from '@/components/UI/Button/Button';
import { Popup } from '@/components/UI/Popup/Popup';
import useScreenSize from '@/hooks/useScreenSize';
import { CommonTestIds } from '@/__e2e__/testIds/common';

export const AdImages = React.forwardRef<AdImagesRef, AdImagesProps>(
  (
    { presetImages, helperText, selected = [], name, onChange },
    adImagesRef
  ) => {
    const { t } = useTranslation();
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isUploadErrorPopupOpen, toggleUploadErrorPopup] =
      useToggleOpenState();
    const hasPresetImages = !!presetImages?.length;
    const { getValues } = useFormContext();
    const handleUploadError = () => toggleUploadErrorPopup(true);
    const {
      sensors,
      onDragEnd,
      onRemove: remove,
      onFileInputChange,
      handleRotate,
    } = useImagesList({
      onChange: handleChange,
      onError: handleUploadError,
    });
    const { isLarge } = useScreenSize();
    const fileInputRef = useRef<HTMLInputElement>(null);

    function handleChange(callback: (prev: AdImage[]) => AdImage[]) {
      onChange(
        callback(((getValues(name) ?? []) as AdImage[]).filter(Boolean))
      );
    }

    function toggleGallery() {
      setIsGalleryOpen(prev => !prev);
    }

    function triggerAdd() {
      if (hasPresetImages) {
        toggleGallery();
      } else {
        fileInputRef.current?.click();
      }
    }

    const AdImagesButton = ({ isFullWidth }: { isFullWidth?: boolean }) => {
      const fullWidthHeight = isLarge ? '200px' : '127px';
      const height = isFullWidth ? fullWidthHeight : 'auto';
      return (
        <S.AdImagesAddButton
          type="button"
          onClick={triggerAdd}
          $height={height}
          $isFullWidth={isFullWidth}
          data-testid={CommonTestIds.adImages.addButton}
        >
          {hasPresetImages ? (
            <IconBox $filename="gallery.svg" $width={35} $height={31} />
          ) : (
            <>
              <input
                type="file"
                data-testid={CommonTestIds.adImages.fileUpload}
                className="pond-new-img-field"
                accept="image/*"
                ref={fileInputRef}
                onChange={onFileInputChange}
                multiple
              />
              <IconBox $filename="camera.svg" $width={37} $height={29} />
            </>
          )}

          {t(
            isFullWidth
              ? hasPresetImages
                ? 'pages.new_ad.choose_photo'
                : 'pages.new_ad.add_photo'
              : hasPresetImages
              ? 'pages.new_ad.choose'
              : 'pages.new_ad.add'
          )}
        </S.AdImagesAddButton>
      );
    };

    const onApplyGallery = (selectedImages: AdImage[]) => {
      onChange(selectedImages);
    };

    useImperativeHandle(
      adImagesRef,
      () => ({
        reset: () => {
          onChange([]);
        },
      }),
      []
    );

    return (
      <S.AdImages name={name}>
        <div id="helper-text-container">
          {helperText && (
            <S.AdImagesHelperTextBox>
              <IconBox
                $height={30}
                $width={30}
                $iconHeight={20}
                $iconWidth={22}
                $filename="warning-2.svg"
              />
              <S.AdImagesHelperText>{helperText}</S.AdImagesHelperText>
            </S.AdImagesHelperTextBox>
          )}
        </div>
        <S.AdImagesGrid>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={selected} strategy={rectSwappingStrategy}>
              {selected.map(image => {
                return (
                  <AdImagesItem
                    key={image.id}
                    image={image}
                    rotate={handleRotate}
                    remove={remove}
                    hasRotationControls={!hasPresetImages}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
          {!!selected.length && <AdImagesButton />}
        </S.AdImagesGrid>

        {!selected.length && <AdImagesButton isFullWidth />}
        <S.AdImagesCaption $variant="2">
          {t('pages.new_ad.add_photo_text_1')}
        </S.AdImagesCaption>
        <S.AdImagesCaption $variant="2">
          {hasPresetImages
            ? t('pages.new_ad.add_photo_text_3')
            : t('pages.new_ad.add_photo_text_2')}
        </S.AdImagesCaption>

        <Popup
          isOpen={isUploadErrorPopupOpen}
          close={() => toggleUploadErrorPopup(false)}
          customSize={{ width: 445 }}
          variant="1"
        >
          <S.AdImagesUploadErrorPopupContent>
            <S.AdImagesUploadErrorPopupTitle>
              {t('pages.new_ad.upload_photo_error_title')}
            </S.AdImagesUploadErrorPopupTitle>
            <S.AdImagesUploadErrorPopupDescription>
              {t('pages.new_ad.upload_photo_error_description')}
            </S.AdImagesUploadErrorPopupDescription>
            <Button
              isFullWidth
              appearance="secondary"
              borderRadius={14}
              onClick={() => toggleUploadErrorPopup(false)}
            >
              {t('common.got_it')}
            </Button>
          </S.AdImagesUploadErrorPopupContent>
        </Popup>

        {hasPresetImages && (
          <PresetPhotoPicker
            isOpen={isGalleryOpen}
            close={toggleGallery}
            onApply={onApplyGallery}
            presetImages={presetImages}
            selectedImages={selected}
          />
        )}
      </S.AdImages>
    );
  }
);

AdImages.displayName = 'AdImages';
